import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub, TbExternalLink } from "solid-icons/tb";
import { createMemo, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { ScrollableFadeContainer } from "../../ui";
import { formatIsoTimestamp } from "../../util/dateFormat";
import { CompleteMarkdown } from "../internal/CompleteMarkdown";
import { type GitHubIssueDetail, parseGitHubIssuePayload } from "./githubIssueSchema";
import { GitHubIssueMeta, safeGitHubUrl } from "./githubRenderUtils";

interface GitHubIssueRendererProps {
  block: StreamingBlock;
}

export const GitHubIssueRenderer = (props: GitHubIssueRendererProps) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<GitHubIssueDetail | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseGitHubIssuePayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {rt.t("github_issue_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
            <div class="border-b border-primary-border px-3 py-2">
              <div class="flex items-baseline gap-2">
                <TbBrandGithub class="size-4 shrink-0 self-center text-primary-text" />
                <span class="shrink-0 font-mono text-xs text-muted-text">{data().reference}</span>
                <span class="truncate text-sm font-medium text-primary-text">{data().title}</span>
                <span class="ml-auto shrink-0 text-xs text-muted-text">{formatIsoTimestamp(data().updatedAt)}</span>
              </div>
              <GitHubIssueMeta issue={data()} />
            </div>

            <Show when={data().body}>
              {(body) => (
                <div class="px-3 py-2">
                  <ScrollableFadeContainer maxHeight="200px">
                    <CompleteMarkdown markdown={body()} />
                  </ScrollableFadeContainer>
                </div>
              )}
            </Show>

            <Show when={safeGitHubUrl(data().url)}>
              {(href) => (
                <div class="border-t border-primary-border px-3 py-2">
                  <a
                    href={href()}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                  >
                    {rt.t("github_issue_card.open_link")}
                    <TbExternalLink class="size-3.5 shrink-0" aria-hidden="true" />
                  </a>
                </div>
              )}
            </Show>
          </div>
        )}
      </Show>
    </Show>
  );
};
